import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Product } from "@/types/product";
import { Pencil } from "lucide-react";

interface EditProductDialogProps {
  product: Product;
  onUpdate: (product: Product) => void;
  trigger?: React.ReactNode;
}

export function EditProductDialog({ product, onUpdate, trigger }: EditProductDialogProps) {
  const [open, setOpen] = useState(false);
  const [installmentError, setInstallmentError] = useState("");
  const [formData, setFormData] = useState({
    deviceName: product.deviceName,
    deviceDescription: product.deviceDescription || "",
    salePrice: product.salePrice.toString(),
    monthlyInstallment: product.monthlyInstallment.toString(),
    currency: product.currency,
    date: product.date,
    notes: product.notes,
  });

  useEffect(() => {
    setFormData({
      deviceName: product.deviceName,
      deviceDescription: product.deviceDescription || "",
      salePrice: product.salePrice.toString(),
      monthlyInstallment: product.monthlyInstallment.toString(),
      currency: product.currency,
      date: product.date,
      notes: product.notes,
    });
    setInstallmentError("");
  }, [product]);

  const handleInstallmentChange = (value: string) => {
    setFormData({ ...formData, monthlyInstallment: value });
    const installment = parseFloat(value) || 0;
    const salePrice = parseFloat(formData.salePrice) || 0;

    if (installment > salePrice) {
      setInstallmentError("قیستی مانگانە نابێت زیاتر بێت لە نرخی فرۆشتن");
    } else {
      setInstallmentError("");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const salePrice = parseFloat(formData.salePrice);
    const monthlyInstallment = parseFloat(formData.monthlyInstallment);

    if (isNaN(salePrice) || salePrice <= 0) {
      setInstallmentError("تکایە نرخی فرۆشتنی دروست دیاری بکە");
      return;
    }

    if (isNaN(monthlyInstallment) || monthlyInstallment <= 0) {
      setInstallmentError("تکایە قیستی مانگانەی دروست دیاری بکە");
      return;
    }

    if (monthlyInstallment > salePrice) {
      setInstallmentError("قیستی مانگانە نابێت زیاتر بێت لە نرخی فرۆشتن");
      return;
    }

    // ئەگەر پارە دراوە، نرخی فرۆشتن نابێت کەمتر بێت لە پارەی دراو
    if (salePrice < product.paidAmount) {
      setInstallmentError(`نرخی فرۆشتن نابێت کەمتر بێت لە پارەی وەرگیراو (${product.paidAmount.toLocaleString()})`);
      return;
    }

    onUpdate({
      ...product,
      deviceName: formData.deviceName,
      deviceDescription: formData.deviceDescription,
      salePrice,
      monthlyInstallment,
      currency: formData.currency,
      remainingAmount: Math.max(0, salePrice - product.paidAmount),
      date: formData.date,
      notes: formData.notes,
    });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      setOpen(newOpen);
      if (!newOpen) setInstallmentError("");
    }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-500/10"
            title="دەستکاریکردن"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>دەستکاری کاڵا</DialogTitle>
          <DialogDescription>زانیاریەکانی کاڵا دەستکاری بکە</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="deviceName">ناوی ئامێر *</Label>
              <Input
                id="deviceName"
                value={formData.deviceName}
                onChange={(e) => setFormData({ ...formData, deviceName: e.target.value })}
                required
                placeholder="ناوی ئامێر"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="deviceDescription">وەسفی ئامێر</Label>
              <Input
                id="deviceDescription"
                value={formData.deviceDescription}
                onChange={(e) => setFormData({ ...formData, deviceDescription: e.target.value })}
                placeholder="وەسفی ئامێر"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="currency">دراو</Label>
            <Input
              id="currency"
              value={formData.currency === "IQD" ? "دینار (IQD)" : "دۆلار (USD)"}
              disabled
              className="bg-muted"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="salePrice">نرخی فرۆشتن</Label>
              <Input
                id="salePrice"
                type="number"
                value={formData.salePrice}
                disabled
                className="bg-muted"
                dir="ltr"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="monthlyInstallment">قیستی مانگانە *</Label>
              <Input
                id="monthlyInstallment"
                type="number"
                value={formData.monthlyInstallment}
                onChange={(e) => handleInstallmentChange(e.target.value)}
                required
                placeholder="0"
                dir="ltr"
                className={installmentError ? "border-destructive" : ""}
              />
              {installmentError && (
                <p className="text-sm text-destructive">{installmentError}</p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="date">بەروار</Label>
            <Input
              id="date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              dir="ltr"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">تێبینی</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="تێبینی..."
              rows={2}
            />
          </div>
          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              پاشگەزبوونەوە
            </Button>
            <Button type="submit" disabled={!!installmentError}>پاشەکەوتکردن</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
